import { observable, action, reaction, makeObservable } from 'mobx';

import history from 'src/history';
import { Path } from 'src/containers/HomeContainer';


export const Version = {
  Picklists: '1.9.2',
  Lookups: '1.18.0',
  Flows: '1.19.7',
  AdminTools: '1.24.1',

  VeryFirst: '1.9.0', // Special version before all our bubbles were introduced
  Current: config.version,
};

const Routes = {
  home: Path.Home1,
  flows: Path.Flow1,
  scheduler: Path.Scheduler,
  context: Path.Context
}

const canSeeFlowsTab = (license) => license.flows.seeTab.can;
const canSeeSchedulerTab = (license) => license.scheduler.seeTab.can;

class CoachmarkStoreImpl {
  @observable bubbles = new Map();
  @observable version = Version.VeryFirst;
  @observable activeSteps = [];

  userStore = null;

  allSteps = [
    // Home
    { id: 'navigation-pane', version: Version.Flows, route: Routes.home,
      title: 'New Layout',
      text: 'We have rearranged things to make it more convenient to access various functions. You can revisit this tour at any time from the add-in menu',
    },
    { id: 'home-icon', version: Version.Flows, route: Routes.home,
      title: 'Home Screen',
      text: 'The Home screen now contains shortcuts to all manual operations with Salesforce data that you can do using XL-Connector',
    },
    { id: 'home-pull-data', version: Version.Flows, route: Routes.home,
      title: 'Pull Data',
      text: 'Run a report or SOQL query choosing to place the data into a new or existing sheet in the active workbook',
      
    },
    { id: 'home-refresh-data', version: Version.Flows, route: Routes.home,
      title: 'Refresh Data',
      text: `Refresh any report or SOQL query that was previously pulled to the active document via XL-Connector 365. Clicking on Refresh All will run all saved flow steps of 'Pull data' type`,
    },
    { id: 'home-create', version: Version.Flows, route: Routes.home,
      title: 'Create or Modify',
      text: `Update, Create, or Upsert records from the active worksheet`,
      isVisible: (license) => license.flowsteps.push.seeHomeButtons.can,
    },
    { id: 'home-delete', version: Version.Flows, route: Routes.home,
      title: 'Delete or Restore',
      text: `Delete or Undelete records in Salesforce based on Ids in the active worksheet`,
      isVisible: (license) => license.flowsteps.push.seeHomeButtons.can,
    },
    { id: 'home-run-flow', version: Version.Flows, route: Routes.home,
      title: 'Run Flows',
      text: `Lets the user choose a flow to run manually. A flow is a way to group several Salesforce operations together`,
      isVisible: (license) => license.flows.run.can,
    },
    { id: 'home-run-all-flows', version: Version.Flows, route: Routes.home,
      title: 'Run All Flows',
      text: `Lets the user run all flows available in the entire active workbook in the order defined under the Flows tab`,
      isVisible: (license) => license.flows.seeRunAllAtHome.can,
    },
    // { id: 'home-admin-tools', version: Version.AdminTools, route: Routes.home,
    //   title: 'Admin Tools',
    //   text: `Use this button to access various tools for Salesforce administrators.`,
    // },


    // Flows
    { id: 'flows-icon', version: Version.Flows, route: Routes.flows,
      title: 'Manage Flows',
      text: `The 'Flows' tab contains all the reusable XL-Connector 365 operations that are available in the active workbook`,
      isVisible: (license) => canSeeFlowsTab(license),
    },
    { id: 'flows-add-flow', version: Version.Flows, route: Routes.flows,
      title: 'Create Flows',
      text: `Create a new custom flow. Custom flows can contain all types of operations`,
      isVisible: (license) => canSeeFlowsTab(license) && license.flows.custom.createOrRemove.can,
    },
    { id: 'flows-edit-flows', version: Version.Flows, route: Routes.flows,
      title: 'Edit Flows',
      text: `Delete custom flows or change the order of execution when 'Run All Flows' button is pressed on the Home screen`,
      isVisible: (license) => canSeeFlowsTab(license) && license.flows.custom.createOrRemove.can,
    },
    { id: 'flows-flows-list', version: Version.Flows, route: Routes.flows,
      title: 'Flow List',
      text: `All flows are shown here. 'Pull Data', 'Push Data' - flows where all 'Pull' and 'Push' operations are saved by default accordingly. All created Custom flows are also shown here` ,
      isVisible: (license) => canSeeFlowsTab(license) && license.flows.seeTabContent.can,
    },
    { id: 'flows-add-flowstep', version: Version.Flows, route: Routes.flows,
      title: 'Create Flowsteps',
      text: `Create a new flow step for the flow selected in the top portion of the screen`,
      isVisible: (license) => canSeeFlowsTab(license) && license.flowsteps.push.createOrRemove.unlimited.can,
    },
    { id: 'flows-edit-flowsteps', version: Version.Flows, route: Routes.flows,
      title: 'Edit Flowsteps',
      text: `Delete flow steps from a flow or change their order of execution`,
      isVisible: (license) => canSeeFlowsTab(license) && license.flowsteps.push.createOrRemove.unlimited.can,
    },
    { id: 'flows-flowsteps-list', version: Version.Flows, route: Routes.flows,
      title: 'Flowstep List',
      text: `All flowsteps of a selected flow are shown here. Click to edit`,
      isVisible: (license) => canSeeFlowsTab(license) && license.flowsteps.push.seeList.can,
    },

    // Scheduler
    { id: 'scheduler-add-action', version: Version.Flows, route: Routes.scheduler,
      title: 'Manage Flows',
      text: `Schedule any flow configured under the Flows tab for automatic offline execution`,
      isVisible: (license) => false && canSeeSchedulerTab(license),
    },

    // Context
    { id: 'context-icon', version: Version.Flows, route: Routes.context,
      title: 'Context Actions',
      text: `In your worksheet, select a value pulled from a field of 'Picklist' or 'Reference' type and use this tab to see and set all available picklist values or search Salesforce for a record Id by record name`,
    },
  ];

  constructor() {
    makeObservable(this);
  }


  @action async startup() {
    this.loadSteps(true);
    this.reactionDisposer = reaction(
      () => ({ license: this.userStore.license.plan, version: this.userStore.settings?.coachmarksVersion }),
      (data) => { // Don't go to the route, because it unconditionally jumps from the the License dialog when trying to change license type
        this.loadSteps(false); 
      } 
    );
  }

  @action clean() {
    this.reactionDisposer?.();
  }

  linkStores(stores) {
    Object.assign(this, stores);
  }

  loadSteps(goToTheFirstStep = true) {
    const { settings, license } = this.userStore;
    this.version = settings?.coachmarksVersion ?? Version.VeryFirst;

    this.activeSteps = this.allSteps
      .filter(({ isVisible, version }) => (isVisible === undefined || isVisible(license)) && compareVersions(version, this.version) > 0)
      .map((x, index, arr) => {
        const { title, text, id, route } = x;
        const isLast = index === arr.length - 1;
        const isFirst = index === 0;

        const teachingBubbleProps = {
          headline: title,
          hasCloseButton: true,
          onDismiss: async () => await this.dismissStep(id),
          primaryButtonProps: {
            text: isLast ? 'Finish' : 'Next',
            onClick: async () => await this.nextStep(id),
          },
          secondaryButtonProps: isLast
            ? null
            : {
              text: 'Skip',
              onClick: async () => await this.skipAllSteps(),
            },
        };

        
        return { id, route, isCollapsed: !isFirst, text, isDismissed: !isFirst, teachingBubbleProps };
      });

    goToTheFirstStep && this.activeSteps.length && this.goToRoute(this.activeSteps[0].route);
  }

  goToRoute(route) {
    const { pathname } = history.location;
    if (route && route !== pathname) {
      history.replace(route);
    } 
  }

  async dismissStep(stepId) {
    await this._nextStep(stepId, true);
  }

  @action async nextStep(stepId) {
    await this._nextStep(stepId, false);
  }

  @action async _nextStep(stepId, collapseNext) {
    const { step, index, isLast } = this._getStep(stepId);
    if (!step) { return; }

    step.isCollapsed = true;
    step.isDismissed = true;

    if (isLast) {
      await this.skipAllSteps();
      return;
    }

    const nextStep = this.activeSteps[index + 1];
    !collapseNext && this.goToRoute(nextStep.route);
    nextStep.isCollapsed = collapseNext;
    nextStep.isDismissed = false;
  }

  @action async skipAllSteps() {
    this.activeSteps = [];
    await this.updateDismissedVersion(Version.Current);
  }

  getStep(stepId) {
    return this._getStep(stepId).step;
  }

  _getStep(stepId) {
    const index = this.activeSteps.findIndex(x => x.id === stepId);
    const step = index === -1 ? null : this.activeSteps[index];
    return { step, index, isLast: index === this.activeSteps.length - 1 };
  }

  @action async updateDismissedVersion(version) {
    if (!this.version || compareVersions(this.version, version) < 0) {
      await this.updateVersion(version);
    }
  }

  @action async updateVersion(version) {
    await this.userStore.updateSettings({
      ...this.userStore.settings,
      coachmarksVersion: version
    });
    this.version = version;
  }

  @action async takeTourAgain() {
    await this.updateVersion(Version.VeryFirst);
    this.goToRoute(Routes.home);
  }
}

function compareVersions(ver1, ver2) {
  const numbers1 = ver1.split('.');
  const numbers2 = ver2.split('.');
  const length = Math.min(numbers1.length, numbers2.length);

  for (let i = 0; i < length; ++i) {
    const n1 = numbers1[i];
    const n2 = numbers2[i];
    const int1 = +n1;
    const int2 = +n2;
    if (!isNaN(int1) && !isNaN(int2)) {
      const diff = int1 - int2;
      if (!diff) { continue; }
      return diff;
    }
    else {
      if (n1 === n2) { continue; }
      return n1 < n2 ? -1 : 1;
    }
  }

  return numbers1.length - numbers2.length;
}


export const CoachmarkStore = CoachmarkStoreImpl;
export default CoachmarkStore;